import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { docsApi } from '../../docs/api';
import { useCreateDoc } from '../../docs/hooks/useDocsData';

// Árbol del Índice del Cuadro de Trabajo (integración main→dev, epic D1.3). El `main`
// original tenía toda esta lógica (fetch + subárbol + jerarquía + búsqueda auto-expandible +
// creación inline) dentro de `WorkframeIndexColumn.jsx` (810 líneas). Acá queda como hook de
// datos/estado; el DnD vive en `useWorkframeIndexDnD.js` y el render en componentes
// presentacionales.
//
// Datos: `docsApi.list` (todo el espacio, o todos los espacios en `/all`) y el filtrado al
// subárbol de `rootDocId` es client-side — el `/refine-plans` confirmó que NO hace falta un
// endpoint nuevo. La key `['docs','list', spaceId]` coincide con `useDocsList` para un espacio
// concreto (cache compartida); la invalidación por prefijo `['docs','list']` de `useDocsData`
// cubre ambas.

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: auto-expande el documento raíz y la ruta de ancestros del documento seleccionado en el árbol del índice.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
function isTrash(doc) {
    return (doc.active_status || 'active') === 'trash';
}

export function useWorkframeIndexTree({ spaceId, rootDocId, selectedDocId }) {
    const isAllSpaces = !spaceId || spaceId === 'all';
    const createDoc = useCreateDoc();

    const listQuery = useQuery({
        queryKey: ['docs', 'list', isAllSpaces ? undefined : spaceId],
        queryFn: async () => {
            const data = await docsApi.list(isAllSpaces ? undefined : { spaceId });
            return data.success && Array.isArray(data.docs) ? data.docs : [];
        },
        refetchOnWindowFocus: false,
    });

    const rawDocs = useMemo(
        () => (listQuery.data || []).filter((d) => !isTrash(d)),
        [listQuery.data],
    );

    const [searchTerm, setSearchTerm] = useState('');
    const [openIds, setOpenIds] = useState(() => new Set(rootDocId ? [rootDocId] : []));
    const [creatingParentId, setCreatingParentId] = useState(null);

    // El doc raíz siempre abierto.
    useEffect(() => {
        if (rootDocId) setOpenIds((prev) => new Set(prev).add(rootDocId));
    }, [rootDocId]);

    // Abrir la ruta de ancestros del documento seleccionado.
    useEffect(() => {
        if (!selectedDocId || rawDocs.length === 0) return;
        let current = rawDocs.find((d) => String(d.id) === String(selectedDocId));
        const ancestors = [];
        while (current && current.parent_id) {
            ancestors.push(current.parent_id);
            current = rawDocs.find((d) => String(d.id) === String(current.parent_id));
        }
        if (ancestors.length > 0) {
            setOpenIds((prev) => {
                const next = new Set(prev);
                ancestors.forEach((id) => next.add(id));
                return next;
            });
        }
    }, [selectedDocId, rawDocs]);

    // Solo los documentos del subárbol de `rootDocId` (BFS sobre `parent_id`).
    const subtreeDocs = useMemo(() => {
        if (!rootDocId || rawDocs.length === 0) return rawDocs;
        const includedIds = new Set([String(rootDocId)]);
        const result = [];
        const rootDoc = rawDocs.find((d) => String(d.id) === String(rootDocId));
        if (rootDoc) result.push(rootDoc);
        let added = true;
        while (added) {
            added = false;
            for (const doc of rawDocs) {
                const docIdStr = String(doc.id);
                const parentIdStr = doc.parent_id ? String(doc.parent_id) : null;
                if (!includedIds.has(docIdStr) && parentIdStr && includedIds.has(parentIdStr)) {
                    includedIds.add(docIdStr);
                    result.push(doc);
                    added = true;
                }
            }
        }
        return result;
    }, [rawDocs, rootDocId]);

    // Jerarquía renderizable.
    const tree = useMemo(() => {
        const docMap = {};
        const roots = [];
        subtreeDocs.forEach((doc) => {
            docMap[doc.id] = {
                ...doc,
                name: doc.title || '',
                type: doc.icon === 'folder' ? 'folder' : 'file',
                children: [],
            };
        });
        subtreeDocs.forEach((doc) => {
            const node = docMap[doc.id];
            if ((rootDocId && String(doc.id) === String(rootDocId)) || !doc.parent_id || !docMap[doc.parent_id]) {
                roots.push(node);
            } else {
                docMap[doc.parent_id].children.push(node);
            }
        });
        const sortNodes = (nodes) => {
            nodes.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
            nodes.forEach((n) => { if (n.children.length > 0) sortNodes(n.children); });
        };
        sortNodes(roots);

        const assignNumbers = (nodes, prefix = '') => {
            nodes.forEach((n, index) => {
                const currentNumber = `${prefix}${index + 1}`;
                n.nodeNumber = currentNumber;
                if (n.children && n.children.length > 0) {
                    assignNumbers(n.children, `${currentNumber}.`);
                }
            });
        };
        assignNumbers(roots);

        return roots;
    }, [subtreeDocs, rootDocId]);

    // Agrupación por espacio para la vista `/all`.
    const groupedTree = useMemo(() => {
        if (!isAllSpaces || tree.length === 0) return null;
        const groups = {};
        tree.forEach((node) => {
            const sId = node.space_id || 'unassigned';
            if (!groups[sId]) {
                groups[sId] = {
                    space_id: sId,
                    space_name: node.space_name || null,
                    space_icon: node.space_icon || null,
                    nodes: [],
                };
            }
            groups[sId].nodes.push(node);
        });
        return Object.values(groups).sort((a, b) => (a.space_name || '').localeCompare(b.space_name || ''));
    }, [tree, isAllSpaces]);

    // Búsqueda: auto-despliega los nodos coincidentes, sus ancestros y sus subdocumentos.
    const searchState = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return { isSearching: false, matchingIds: new Set(), expandedIds: new Set(), visibleIds: new Set() };
        }
        const matchingIds = new Set();
        const expandedIds = new Set();
        const visibleIds = new Set();
        subtreeDocs.forEach((d) => {
            const title = (d.title || d.name || '').toLowerCase();
            if (title.includes(term)) {
                matchingIds.add(d.id);
                visibleIds.add(d.id);
            }
        });
        matchingIds.forEach((id) => {
            let current = subtreeDocs.find((d) => d.id === id);
            while (current && current.parent_id) {
                expandedIds.add(current.parent_id);
                visibleIds.add(current.parent_id);
                current = subtreeDocs.find((d) => d.id === current.parent_id);
            }
        });
        matchingIds.forEach((id) => {
            expandedIds.add(id);
            const addSubdocs = (parentId) => {
                subtreeDocs.forEach((d) => {
                    if (d.parent_id === parentId) {
                        visibleIds.add(d.id);
                        expandedIds.add(d.id);
                        addSubdocs(d.id);
                    }
                });
            };
            addSubdocs(id);
        });
        return { isSearching: true, matchingIds, expandedIds, visibleIds };
    }, [searchTerm, subtreeDocs]);

    const toggleOpen = useCallback((id) => {
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const parentIds = useMemo(
        () => subtreeDocs
            .filter((d) => subtreeDocs.some((child) => String(child.parent_id) === String(d.id)))
            .map((d) => d.id),
        [subtreeDocs],
    );

    const isAllExpanded = useMemo(() => {
        if (parentIds.length === 0) {
            return subtreeDocs.length > 0 && subtreeDocs.every((d) => openIds.has(d.id));
        }
        return parentIds.every((id) => openIds.has(id));
    }, [parentIds, subtreeDocs, openIds]);

    const toggleExpandAll = useCallback(() => {
        setOpenIds((prev) => {
            const allOpen = parentIds.length === 0
                ? subtreeDocs.length > 0 && subtreeDocs.every((d) => prev.has(d.id))
                : parentIds.every((id) => prev.has(id));
            if (allOpen) return new Set(rootDocId ? [rootDocId] : []);
            return new Set(subtreeDocs.map((d) => d.id));
        });
    }, [parentIds, subtreeDocs, rootDocId]);

    const createSubdoc = useCallback(async (parentId, title, { spaceId: docSpaceId } = {}) => {
        const res = await createDoc.mutateAsync({
            title: title?.trim() || '',
            space_id: docSpaceId || (isAllSpaces ? undefined : spaceId),
            parent_id: parentId,
            icon: 'file',
            content: '',
        });
        setCreatingParentId(null);
        if (parentId) setOpenIds((prev) => new Set(prev).add(parentId));
        return res?.doc || null;
    }, [createDoc, spaceId, isAllSpaces]);

    return {
        rawDocs,
        tree,
        groupedTree,
        searchState,
        searchTerm,
        setSearchTerm,
        openIds,
        toggleOpen,
        isAllExpanded,
        toggleExpandAll,
        creatingParentId,
        setCreatingParentId,
        createSubdoc,
        isLoading: listQuery.isLoading,
    };
}
